import React, { useEffect } from 'react'
import {
  Alert,
  Box,
  Divider,
  IconButton,
  Paper,
  Typography
} from '@mui/material'
import { ChevronLeft } from '@mui/icons-material'

import { useAuth } from '../../components/AuthContext/AuthProvider'
import { AuthCredentials } from '../../components/AuthContext/types'
import { Link, useNavigate } from 'react-router-dom'
import { APP_PATHS } from '../../components/AppRouter/types'
import SignUpComponent from './SignUp.component'
import { MainPageBox } from '../../components/DisplayHelpers/MainPageBox'
import { ProgressStepper } from '../../components/ProgressStepper/Progress.component'

import { userAlreadyCreated } from '../../services/user.session.service'
import {
  sendTrackingEvent,
  toButtonEvent,
  toNavigationEvent,
  toScreenEvent
} from '../../services/amplitude.service'
import {
  LoginButtonClickedEvent,
  NavigationEvent,
  NextButtonClickedEvent,
  ScreenOpenedEvent
} from './types'

const SignUpContainer: React.FC = (): JSX.Element => {
  const { signUp, authError, loading, user } = useAuth()
  const navigate = useNavigate()

  sendTrackingEvent(toScreenEvent(ScreenOpenedEvent))

  const handleOnSubmit = (data: AuthCredentials) => {
    sendTrackingEvent(toButtonEvent({ ...NextButtonClickedEvent }))
    signUp(data)
  }

  const alreadyExists = (): boolean =>
    !!authError && authError == userAlreadyCreated

  const handleBack = (): void => {
    sendTrackingEvent(toNavigationEvent(NavigationEvent))
    navigate(APP_PATHS.QUALIFYING_DEVICE)
  }

  /**
   * Better to route from a container than a parent
   * provider component
   */
  useEffect(() => {
    if (user && !loading) {
      navigate(APP_PATHS.APPLICATION, { replace: true })
    }
  }, [user])

  return (
    <MainPageBox>
      <Paper
        elevation={2}
        sx={{
          maxWidth: 600,
          minWidth: 300,
          pb: 2.5
        }}
      >
        <Box sx={{ p: 5 }}>
          <Box sx={{ mt: -4, mb: 2, display: 'flex', pr: 4, pl: 2 }}>
            <IconButton
              size="small"
              color="primary"
              sx={{ ml: -4 }}
              onClick={handleBack}
            >
              <ChevronLeft />
            </IconButton>
            <ProgressStepper step={0} />
          </Box>

          <Divider sx={{ mt: -2.5, mr: -5, ml: -5 }} variant="fullWidth" />

          <SignUpComponent onSubmit={handleOnSubmit} loading={loading} />

          <Typography
            component="div"
            variant="body1"
            align="center"
            sx={{ mt: 2 }}
          >
            Already have an account?{' '}
            <Link
              to={APP_PATHS.LOGIN_IN}
              style={{ textDecoration: 'none' }}
              onClick={() =>
                sendTrackingEvent(toButtonEvent(LoginButtonClickedEvent))
              }
            >
              Log In
            </Link>{' '}
            here
          </Typography>

          <Box sx={{ marginTop: 2, maxWidth: 300 }}>
            {alreadyExists() ? (
              <Alert severity="warning">
                <Typography variant="body2">
                  Looks like your account already exists. Please{' '}
                  <Link
                    to={APP_PATHS.LOGIN_IN}
                    style={{ textDecoration: 'none' }}
                  >
                    Log In
                  </Link>
                </Typography>
              </Alert>
            ) : authError ? (
              <Alert severity="error">{authError}</Alert>
            ) : (
              <></>
            )}
          </Box>
        </Box>
      </Paper>
    </MainPageBox>
  )
}

export default SignUpContainer
